import React, { useEffect, useState } from 'react';
import { getFiles } from '../../../hooks/api-methods/get-upload';
import { deleteFile } from '../../../hooks/api-methods/delete-upload';

import MyMasonry from '../../../components/Mansory/Mansory';
import Loader from '../../../components/Loader/Loader';
import ConfirmModal from '../../../components/PopUps/ConfirmModal';
import { Trash2 } from 'lucide-react';

interface FileItem {
  name: string;
  url: string;
}

interface UploadsViewProps {
  selectMode?: boolean; // optional: true if selecting images
  selectedImages?: string[];
  onSelect?: (url: string) => void;
}

const UploadsView: React.FC<UploadsViewProps> = ({ selectMode, selectedImages = [], onSelect }) => {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileItem | null>(null);

  async function fetchFiles() {
    try {
      setLoading(true);
      setError(null);
      const result = await getFiles('uploads');
      setFiles(result);
    } catch (err) {
      if (err instanceof Error) setError(err.message);
      else setError('Er is een onbekende fout opgetreden.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchFiles();
  }, []);

  // Open modal
  function confirmDelete(file: FileItem) {
    setSelectedFile(file);
    setShowModal(true);
  }

  // Confirm deletion
  async function handleConfirmDelete() {
    if (!selectedFile) return;
    try {
      setLoading(true);
      await deleteFile('uploads', selectedFile.name);
      await fetchFiles();
    } catch (error) {
      console.error(error);
      alert('Verwijderen mislukt.');
    } finally {
      setShowModal(false);
      setSelectedFile(null);
      setLoading(false);
    }
  }

  const handleSelectImage = (url: string) => {
    if (!selectMode || !onSelect) return;
    onSelect(url);
  };

  if (loading) return <Loader />;
  if (error) return <p className="flex-row-align justify-center w-100 vh-75">{error}</p>;
  if (files.length === 0) return <p className="w-100 vh-75">Geen bestanden gevonden.</p>;

  return (
    <div className="ptb-2 relative">
      <MyMasonry
        items={files}
        renderItem={(file: FileItem) => {
          const isSelected = selectedImages?.includes(file.url);

          return (
            <div
              key={file.url}
              className={`colomn-4 relative group ${selectMode && isSelected ? 'selected' : ''}`}
              onClick={() => handleSelectImage(file.url)}
            >
              {file.url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                <img className="w-100 slider-img" src={file.url.trim()} alt={file.name} />
              ) : (
                <a
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  {file.name}
                </a>
              )}

              {!selectMode && (
                <button onClick={() => confirmDelete(file)} className="button-delete">
                  <Trash2 />
                </button>
              )}

              {selectMode && isSelected && <span className="selected-badge">✓</span>}
            </div>
          );
        }}
      />

      <ConfirmModal
        isOpen={showModal}
        title="Bestand verwijderen"
        message={`Weet je zeker dat je '${selectedFile?.name}' wilt verwijderen?`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowModal(false)}
      />
    </div>
  );
};

export default UploadsView;
